import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {Modal} from 'react-bootstrap'
import {Formik, Form, Field, ErrorMessage} from 'formik'
import * as Yup from 'yup'
import {useQuery, useQueryClient} from 'react-query'
import {useListView} from '../core/ListViewProvider'
import {createOne, getOneById, patchOne} from '../../../../services/common_requests'
import {toast} from 'react-toastify'
import {useAuth} from '../../../../modules/auth'
import {isSuperAdmin, ROLES, EDITOR_ROLES} from '../../../../utils/roleUtils'
import {UserConnectionInsights} from '../components/UserConnectionInsights'

type RoleOption = {_id: string; id: number; name: string}

/** Roles never assignable from the team (équipe) screen */
const TEAM_ROLE_DENY = new Set(['ROLE_USER', 'ROLE_HOTEL_MANAGER'])
/** Only Super Admin can assign these roles */
const SUPER_ADMIN_EXCLUSIVE_ROLES = new Set([ROLES.SUPER_ADMIN, 'ROLE_MARKETPLACE'])

const ROLE_ICON: Record<string, string> = {
  ROLE_SUPER_ADMIN: 'admin_panel_settings',
  ROLE_ADMIN: 'verified_user',
  ROLE_EDITOR_BUNDLE: 'edit_square',
  ROLE_EDITOR: 'edit_square',
  ROLE_RESERVATION: 'calendar_month',
  ROLE_RECEPTIONIST: 'calendar_month',
  ROLE_HOTEL_MANAGER: 'hotel',
  ROLE_MARKETPLACE: 'storefront',
}

// ── Reusable field components ──────────────────────────────────────────────

const AzLabel: React.FC<{children: React.ReactNode}> = ({children}) => (
  <label
    style={{
      display: 'block',
      fontSize: '0.6875rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      color: '#434652',
      marginBottom: '0.375rem',
      fontFamily: 'Inter, sans-serif',
    }}
  >
    {children}
  </label>
)

const fieldStyle: React.CSSProperties = {
  width: '100%',
  backgroundColor: '#f2f4f6',
  border: 'none',
  borderRadius: '0.75rem',
  padding: '0.75rem 1rem',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: '#191c1e',
  outline: 'none',
  transition: 'box-shadow 0.15s',
  fontFamily: 'Inter, sans-serif',
}

const fieldWithIconStyle: React.CSSProperties = {
  ...fieldStyle,
  paddingLeft: '2.75rem',
}

const AzFieldWrapper: React.FC<{icon?: string; children: React.ReactNode}> = ({icon, children}) => (
  <div style={{position: 'relative'}}>
    {icon && (
      <span
        className='material-symbols-outlined'
        style={{
          position: 'absolute',
          left: '0.875rem',
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: '18px',
          color: '#737783',
          pointerEvents: 'none',
        }}
      >
        {icon}
      </span>
    )}
    {children}
  </div>
)

// ── Main Component ────────────────────────────────────────────────────────

export const UserFormModal: React.FC = () => {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const {auth} = useAuth()
  const currentRole = auth?.role || localStorage.getItem('role') || ''
  const currentUserIsSuperAdmin = isSuperAdmin(currentRole)

  const {itemIdForUpdate, setItemIdForUpdate} = useListView()
  const isCreate = itemIdForUpdate === 'new'
  const editId = !isCreate && itemIdForUpdate ? itemIdForUpdate : null

  const [roles, setRoles] = useState<RoleOption[]>([])

  const {data: existingUser, isLoading: isLoadingUser} = useQuery(
    ['user-edit', editId],
    () => getOneById(editId as string, `/users`),
    {enabled: !!editId && !isCreate},
  )

  const formatRoleLabel = useCallback(
    (name: string) =>
      intl.formatMessage({
        id: `USERS.ROLE.${name}`,
        defaultMessage: name.replace(/^ROLE_/, '').replace(/_/g, ' '),
      }),
    [intl],
  )

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/roles`, {
          headers: {Authorization: `bearer ${localStorage.getItem('token') || ''}`},
        })
        const data = await res.json()
        const list = Array.isArray(data) ? data : data?.data || []
        // Base filter: for create (team add) deny client roles; for edit allow user's current to avoid UI conflict with clients list
        let filtered = (list as RoleOption[]).filter((r) => {
          if (isCreate && TEAM_ROLE_DENY.has(r.name)) return false
          if (!currentUserIsSuperAdmin && SUPER_ADMIN_EXCLUSIVE_ROLES.has(r.name)) {
            return false
          }
          return true
        })
        // On edit: ensure the edited user's existing roles (e.g. ROLE_USER from clients list) are present in UI even if normally denied
        if (!isCreate && existingUser) {
          const existingNames = new Set<string>()
          const roleList = (existingUser as any)?.roles
          if (Array.isArray(roleList)) {
            roleList.forEach((r: any) => {
              const nm = typeof r === 'object' ? r?.name : undefined
              if (nm) existingNames.add(nm)
            })
          }
          const primary = (existingUser as any)?.role
          const primaryName = typeof primary === 'object' ? primary?.name : typeof primary === 'string' ? primary : undefined
          if (primaryName) existingNames.add(primaryName)
          const toAdd = (list as RoleOption[]).filter(
            (r) => existingNames.has(r.name) && !filtered.some((f) => f._id === r._id)
          )
          if (toAdd.length) {
            filtered = [...filtered, ...toAdd]
          }
        }
        setRoles(filtered)
      } catch {
        setRoles([])
      }
    }
    if (itemIdForUpdate) loadRoles()
  }, [itemIdForUpdate, currentUserIsSuperAdmin, isCreate, existingUser])

  const initialValues = useMemo(() => {
    if (isCreate) {
      return {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        roleIds: [] as string[],
        gender: 'M' as const,
        preferredLanguage: 'fr',
      }
    }
    const roleList = (existingUser as any)?.roles
    const idsFromRoles = Array.isArray(roleList)
      ? roleList.map((r: any) => (typeof r === 'object' ? r._id : r)).filter(Boolean)
      : []
    const primary = (existingUser?.role as any)?._id || (existingUser?.role as any)?.id || ''
    let roleIds = idsFromRoles.length > 0 ? idsFromRoles : primary ? [primary] : []
    const editorIdsLoaded = roles
      .filter((r) => EDITOR_ROLES.includes(r.name))
      .map((r) => r._id)
    const hasEditor =
      editorIdsLoaded.length > 0 &&
      editorIdsLoaded.some((id) => roleIds.map(String).includes(String(id)))
    if (hasEditor) {
      const merged = new Set([...roleIds.map(String), ...editorIdsLoaded.map(String)])
      roleIds = Array.from(merged)
    }
    const g = (existingUser as any)?.gender
    const gender = g === 'F' || g === 'M' ? g : 'M'
    const pl = String((existingUser as any)?.preferredLanguage || 'fr').toLowerCase()
    const preferredLanguage = pl === 'en' ? 'en' : 'fr'
    return {
      firstName: existingUser?.firstName || '',
      lastName: existingUser?.lastName || '',
      email: existingUser?.email || '',
      phoneNumber: (existingUser as any)?.phoneNumber || '',
      roleIds: roleIds as string[],
      gender,
      preferredLanguage,
    }
  }, [isCreate, existingUser, roles])

  const validationSchema = Yup.object({
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    email: Yup.string().email().required(),
    phoneNumber: Yup.string().required(),
    roleIds: Yup.array().of(Yup.string()).min(1),
  })

  const handleClose = () => setItemIdForUpdate(undefined)

  if (!itemIdForUpdate) return null

  return (
    <Modal
      show
      onHide={handleClose}
      size='lg'
      backdrop='static'
      centered
      contentClassName='border-0 overflow-hidden'
    >
      {!isCreate && editId && isLoadingUser && (
        <div
          style={{
            padding: '3rem 2rem',
            textAlign: 'center',
            color: '#434652',
            fontSize: '0.9375rem',
          }}
        >
          {intl.formatMessage({id: 'USERS.FORM.LOADING', defaultMessage: 'Loading user…'})}
        </div>
      )}
      {!isCreate && editId && !isLoadingUser && !existingUser && (
        <div style={{padding: '2rem', color: '#ba1a1a', fontSize: '0.875rem'}}>
          {intl.formatMessage({
            id: 'USERS.FORM.LOAD_ERROR',
            defaultMessage: 'Could not load this user. Try again or check your connection.',
          })}
        </div>
      )}
      {(isCreate || (editId && existingUser && !isLoadingUser)) && (
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, {setSubmitting}) => {
          try {
            if (isCreate) {
              const fd = new FormData()
              fd.append('firstName', values.firstName)
              fd.append('lastName', values.lastName)
              fd.append('email', values.email)
              fd.append('phoneNumber', values.phoneNumber)
              fd.append('roleIds', JSON.stringify(values.roleIds))
              fd.append('gender', values.gender)
              fd.append('preferredLanguage', values.preferredLanguage)
              fd.append('termsAndConditions', 'true')
              fd.append('status', 'ACTIVE')
              const res = await createOne(fd, '/users')
              if (res?.payload?.code === 2021) {
                toast.success(intl.formatMessage({id: 'USERS.FORM.SUCCESS_CREATE'}))
                queryClient.invalidateQueries(['users'])
                handleClose()
              } else {
                toast.error(
                  (res as any)?.payload?.message ||
                    intl.formatMessage({id: 'USERS.FORM.ERROR_SAVE'}),
                )
              }
            } else if (editId) {
              const fd = new FormData()
              fd.append('firstName', values.firstName)
              fd.append('lastName', values.lastName)
              // Email is immutable in back-office (Équipe) edit
              fd.append('email', existingUser?.email || values.email)
              fd.append('phoneNumber', values.phoneNumber)
              fd.append('roleIds', JSON.stringify(values.roleIds))
              fd.append('gender', values.gender)
              fd.append('preferredLanguage', values.preferredLanguage)
              const res = await patchOne(fd, `/users/${editId}`)
              if (res?.payload?.code === 2021) {
                toast.success(intl.formatMessage({id: 'USERS.FORM.SUCCESS_UPDATE'}))
                queryClient.invalidateQueries(['users'])
                queryClient.invalidateQueries(['user-edit', editId])
                handleClose()
              } else {
                toast.error(
                  (res as any)?.payload?.message ||
                    intl.formatMessage({id: 'USERS.FORM.ERROR_SAVE'}),
                )
              }
            }
          } finally {
            setSubmitting(false)
          }
        }}
      >
        {({isSubmitting, values, setFieldValue}) => {
          const editorRolesInList = roles.filter((r) => EDITOR_ROLES.includes(r.name))
          const editorIds = editorRolesInList.map((r) => r._id)
          const nonEditorRoles = roles.filter((r) => !EDITOR_ROLES.includes(r.name))
          const editorFullySelected =
            editorIds.length > 0 && editorIds.every((id) => values.roleIds.includes(id))
          const toggleEditorBundle = () => {
            if (editorIds.length === 0) return
            if (editorFullySelected) {
              setFieldValue(
                'roleIds',
                values.roleIds.filter((id) => !editorIds.includes(id)),
              )
            } else {
              setFieldValue(
                'roleIds',
                Array.from(new Set([...values.roleIds, ...editorIds])),
              )
            }
          }
          return (
          <Form>
            {/* ── Modal shell ───────────────────────────────────────── */}
            <div
              style={{
                backgroundColor: 'rgba(255,255,255,0.97)',
                borderRadius: '1.25rem',
              }}
            >
              {/* ── Header ──────────────────────────────────────────── */}
              <div
                style={{
                  padding: '2rem 2rem 1.5rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <div>
                  <h2
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontSize: '1.5rem',
                      fontWeight: 800,
                      color: '#00347f',
                      margin: 0,
                      letterSpacing: '-0.02em',
                      lineHeight: 1.2,
                    }}
                  >
                    {isCreate
                      ? intl.formatMessage({id: 'USERS.FORM.CREATE_TITLE'})
                      : intl.formatMessage({id: 'USERS.FORM.EDIT_TITLE'})}
                  </h2>
                  <p style={{color: '#434652', fontSize: '0.875rem', margin: '0.375rem 0 0'}}>
                    {isCreate
                      ? intl.formatMessage({id: 'USERS.FORM.CREATE_HINT'})
                      : intl.formatMessage({id: 'USERS.FORM.EDIT_HINT', defaultMessage: 'Update the professional profile details.'})}
                  </p>
                </div>
                <button
                  type='button'
                  onClick={handleClose}
                  style={{
                    width: '2.25rem',
                    height: '2.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    borderRadius: '50%',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    color: '#737783',
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#f2f4f6')
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent')
                  }
                >
                  <span className='material-symbols-outlined' style={{fontSize: '20px'}}>
                    close
                  </span>
                </button>
              </div>

              {/* ── Body ────────────────────────────────────────────── */}
              <div style={{padding: '0 2rem 2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>

                {/* Create hint banner */}
                {isCreate && (
                  <div
                    style={{
                      display: 'flex',
                      gap: '0.75rem',
                      padding: '1rem',
                      backgroundColor: 'rgba(176, 198, 255, 0.2)',
                      borderRadius: '0.75rem',
                      borderLeft: '4px solid #00347f',
                    }}
                  >
                    <span
                      className='material-symbols-outlined'
                      style={{
                        fontSize: '20px',
                        color: '#094298',
                        flexShrink: 0,
                        fontVariationSettings: "'FILL' 1",
                      }}
                    >
                      info
                    </span>
                    <p style={{margin: 0, fontSize: '0.8125rem', color: '#094298', lineHeight: 1.5}}>
                      {intl.formatMessage({id: 'USERS.FORM.CREATE_PASSWORD_HINT', defaultMessage: 'A temporary password is emailed to the user. They will be prompted to change it upon their first login.'})}
                    </p>
                  </div>
                )}

                {/* Edit: read-only info banner */}
                {!isCreate && existingUser && (
                  <UserConnectionInsights
                    status={(existingUser as any).status}
                    lastConnection={(existingUser as any).lastConnection}
                    numConnections={(existingUser as any).numConnections}
                    connectionSummary={(existingUser as any).connectionSummary}
                    connectionHistory={(existingUser as any).connectionHistory}
                  />
                )}

                {/* Fields grid */}
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem 2rem'}}>

                  {/* First name */}
                  <div>
                    <AzLabel>{intl.formatMessage({id: 'USERS.FORM.FIRST_NAME'})}</AzLabel>
                    <Field
                      name='firstName'
                      placeholder='E.g. Selim'
                      style={fieldStyle}
                      onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                        (e.target.style.boxShadow = '0 0 0 2px rgba(0, 104, 118, 0.4)')
                      }
                      onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                        (e.target.style.boxShadow = 'none')
                      }
                    />
                    <ErrorMessage name='firstName'>{(msg) => <div style={{color: '#ba1a1a', fontSize: '0.75rem', marginTop: '0.25rem'}}>{msg}</div>}</ErrorMessage>
                  </div>

                  {/* Last name */}
                  <div>
                    <AzLabel>{intl.formatMessage({id: 'USERS.FORM.LAST_NAME'})}</AzLabel>
                    <Field
                      name='lastName'
                      placeholder='E.g. Touil'
                      style={fieldStyle}
                      onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                        (e.target.style.boxShadow = '0 0 0 2px rgba(0, 104, 118, 0.4)')
                      }
                      onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                        (e.target.style.boxShadow = 'none')
                      }
                    />
                    <ErrorMessage name='lastName'>{(msg) => <div style={{color: '#ba1a1a', fontSize: '0.75rem', marginTop: '0.25rem'}}>{msg}</div>}</ErrorMessage>
                  </div>

                  {/* Email */}
                  <div>
                    <AzLabel>Email</AzLabel>
                    <AzFieldWrapper icon='mail'>
                      <Field
                        name='email'
                        type='email'
                        placeholder='selim@touiltravel.com'
                        style={{
                          ...fieldWithIconStyle,
                          ...(isCreate
                            ? {}
                            : {backgroundColor: '#f6f7fb', cursor: 'not-allowed', opacity: 0.9}),
                        }}
                        disabled={!isCreate}
                        readOnly={!isCreate}
                        onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                          (e.target.style.boxShadow = '0 0 0 2px rgba(0, 104, 118, 0.4)')
                        }
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                          (e.target.style.boxShadow = 'none')
                        }
                      />
                    </AzFieldWrapper>
                    <ErrorMessage name='email'>{(msg) => <div style={{color: '#ba1a1a', fontSize: '0.75rem', marginTop: '0.25rem'}}>{msg}</div>}</ErrorMessage>
                  </div>

                  {/* Phone */}
                  <div>
                    <AzLabel>{intl.formatMessage({id: 'USERS.FORM.PHONE'})}</AzLabel>
                    <AzFieldWrapper icon='call'>
                      <Field
                        name='phoneNumber'
                        placeholder='+216 20 000 000'
                        style={fieldWithIconStyle}
                        onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                          (e.target.style.boxShadow = '0 0 0 2px rgba(0, 104, 118, 0.4)')
                        }
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                          (e.target.style.boxShadow = 'none')
                        }
                      />
                    </AzFieldWrapper>
                    <ErrorMessage name='phoneNumber'>{(msg) => <div style={{color: '#ba1a1a', fontSize: '0.75rem', marginTop: '0.25rem'}}>{msg}</div>}</ErrorMessage>
                  </div>

                  {/* Role cards — full width */}
                  <div style={{gridColumn: '1 / -1'}}>
                    <AzLabel>
                      {intl.formatMessage({id: 'USERS.FORM.ROLE'})}
                      <span style={{color: '#737783', fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: '0.5rem', fontSize: '0.6875rem'}}>
                        {intl.formatMessage({id: 'USERS.FORM.ROLE_HINT'})}
                      </span>
                    </AzLabel>
                    {roles.length === 0 ? (
                      <p style={{fontSize: '0.8125rem', color: '#737783', margin: 0}}>Loading roles…</p>
                    ) : (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                          gap: '0.625rem',
                        }}
                      >
                        {editorIds.length > 0 && (
                          <button
                            key='__editor_bundle__'
                            type='button'
                            onClick={toggleEditorBundle}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.625rem',
                              padding: '0.625rem 0.875rem',
                              backgroundColor: editorFullySelected ? '#a1efff' : '#f2f4f6',
                              border: `2px solid ${editorFullySelected ? '#006876' : 'transparent'}`,
                              borderRadius: '0.75rem',
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                              textAlign: 'left',
                            }}
                          >
                            <span
                              className='material-symbols-outlined'
                              style={{
                                fontSize: '18px',
                                color: '#006876',
                                flexShrink: 0,
                                fontVariationSettings: editorFullySelected
                                  ? "'FILL' 1"
                                  : "'FILL' 0",
                              }}
                            >
                              {ROLE_ICON.ROLE_EDITOR_BUNDLE}
                            </span>
                            <div>
                              <p style={{margin: 0, fontSize: '0.8125rem', fontWeight: 700, color: '#191c1e', lineHeight: 1.2}}>
                                {intl.formatMessage({
                                  id: 'USERS.ROLE.ROLE_EDITOR_BUNDLE',
                                  defaultMessage: 'Éditeur (Hôtels, circuits, pages)',
                                })}
                              </p>
                            </div>
                          </button>
                        )}
                        {nonEditorRoles.map((role) => {
                          const isSelected = values.roleIds.includes(role._id)
                          const icon = ROLE_ICON[role.name] || 'badge'
                          return (
                            <button
                              key={role._id}
                              type='button'
                              onClick={() => {
                                const next = isSelected
                                  ? values.roleIds.filter((id) => id !== role._id)
                                  : [...values.roleIds, role._id]
                                setFieldValue('roleIds', next)
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.625rem',
                                padding: '0.625rem 0.875rem',
                                backgroundColor: isSelected ? '#a1efff' : '#f2f4f6',
                                border: `2px solid ${isSelected ? '#006876' : 'transparent'}`,
                                borderRadius: '0.75rem',
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                                textAlign: 'left',
                              }}
                            >
                              <span
                                className='material-symbols-outlined'
                                style={{
                                  fontSize: '18px',
                                  color: '#006876',
                                  flexShrink: 0,
                                  fontVariationSettings: isSelected
                                    ? "'FILL' 1"
                                    : "'FILL' 0",
                                }}
                              >
                                {icon}
                              </span>
                              <div>
                                <p style={{margin: 0, fontSize: '0.8125rem', fontWeight: 700, color: '#191c1e', lineHeight: 1.2}}>
                                  {formatRoleLabel(role.name)}
                                </p>
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    )}
                    <ErrorMessage name='roleIds'>{(msg) => <div style={{color: '#ba1a1a', fontSize: '0.75rem', marginTop: '0.375rem'}}>{msg}</div>}</ErrorMessage>
                  </div>

                  {/* Language */}
                  <div>
                    <AzLabel>{intl.formatMessage({id: 'USERS.FORM.PREFERRED_LANGUAGE'})}</AzLabel>
                    <div style={{position: 'relative'}}>
                      <Field
                        as='select'
                        name='preferredLanguage'
                        style={{...fieldStyle, paddingRight: '2.5rem', appearance: 'none', cursor: 'pointer'}}
                      >
                        <option value='fr'>{intl.formatMessage({id: 'USERS.LANG.FR'})}</option>
                        <option value='en'>{intl.formatMessage({id: 'USERS.LANG.EN'})}</option>
                      </Field>
                      <span
                        className='material-symbols-outlined'
                        style={{
                          position: 'absolute',
                          right: '0.875rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          fontSize: '18px',
                          color: '#737783',
                          pointerEvents: 'none',
                        }}
                      >
                        expand_more
                      </span>
                    </div>
                  </div>

                  {/* Gender segmented control */}
                  <div>
                    <AzLabel>{intl.formatMessage({id: 'USERS.FORM.GENDER'})}</AzLabel>
                    <div
                      style={{
                        display: 'flex',
                        gap: '0.25rem',
                        padding: '0.25rem',
                        backgroundColor: '#f2f4f6',
                        borderRadius: '0.75rem',
                        width: 'fit-content',
                      }}
                    >
                      {([
                        {value: 'M', label: intl.formatMessage({id: 'USERS.GENDER.M'})},
                        {value: 'F', label: intl.formatMessage({id: 'USERS.GENDER.F'})},
                      ] as const).map((opt) => {
                        const active = values.gender === opt.value
                        return (
                          <button
                            key={opt.value}
                            type='button'
                            onClick={() => setFieldValue('gender', opt.value)}
                            style={{
                              padding: '0.375rem 1rem',
                              borderRadius: '0.5rem',
                              border: 'none',
                              fontSize: '0.8125rem',
                              fontWeight: active ? 700 : 500,
                              cursor: 'pointer',
                              backgroundColor: active ? '#ffffff' : 'transparent',
                              color: active ? '#00347f' : '#737783',
                              boxShadow: active ? '0 1px 4px rgba(0,52,127,0.1)' : 'none',
                              transition: 'all 0.15s',
                            }}
                          >
                            {opt.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                </div>
              </div>

              {/* ── Footer ──────────────────────────────────────────── */}
              <div
                style={{
                  padding: '1rem 2rem 1.5rem',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '0.75rem',
                  alignItems: 'center',
                }}
              >
                <button
                  type='button'
                  onClick={handleClose}
                  style={{
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.75rem',
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: '#434652',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    fontFamily: 'Inter, sans-serif',
                  }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#f2f4f6')
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent')
                  }
                >
                  {intl.formatMessage({id: 'ACTION.CLOSE'})}
                </button>
                <button
                  type='submit'
                  disabled={isSubmitting}
                  style={{
                    padding: '0.75rem 2rem',
                    borderRadius: '0.75rem',
                    border: 'none',
                    background: 'linear-gradient(135deg, #00347f 0%, #1a4ba1 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting ? 0.7 : 1,
                    boxShadow: '0 4px 16px -4px rgba(0, 52, 127, 0.4)',
                    fontFamily: 'Inter, sans-serif',
                    transition: 'all 0.15s',
                  }}
                >
                  {isSubmitting ? '…' : intl.formatMessage({id: 'USERS.FORM.SUBMIT'})}
                </button>
              </div>

              {/* ── Bottom accent bar ────────────────────────────────── */}
              <div
                style={{
                  height: '6px',
                  background: 'linear-gradient(to right, #006876, #1a4ba1, #ffb870)',
                }}
              />
            </div>
          </Form>
        )
        }}
      </Formik>
      )}
    </Modal>
  )
}
